import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { describeHlsError, describeMediaError } from "./playerErrorDetails";

const STORAGE_KEY = "ottkit:player";

interface StoredPrefs {
  volume?: number;
  muted?: boolean;
}

function readPrefs(): StoredPrefs {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || "{}",
    ) as StoredPrefs;
  } catch {
    return {};
  }
}

function writePrefs(prefs: StoredPrefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readPrefs(), ...prefs }),
    );
  } catch {
    // storage unavailable
  }
}

// CMCD sid: один на инстанс плеера; crypto.randomUUID нет в старых WebView.
function createSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// CMCD включаем только для Cloudflare Stream: у Bunny (xxxkit) подпись
// ссылки может покрывать query-параметры, лишний CMCD= сломал бы плеер.
export function isCloudflareStreamSource(src: string): boolean {
  try {
    const host = new URL(src, "https://ottkit.io").hostname;
    return (
      host.endsWith(".cloudflarestream.com") ||
      host === "videodelivery.net" ||
      host.endsWith(".videodelivery.net")
    );
  } catch {
    return false;
  }
}

// Native <video> (Safari-native-HLS, no hls.js instance involved) live-edge
// signal: video.seekable's end. Shared by the durationchange/progress
// listeners' patchNativeLiveEdge and by goLive()'s fallback when hls.js
// doesn't have a liveSyncPosition.
function computeNativeLiveEdge(video: HTMLVideoElement): number | null {
  return video.seekable.length
    ? video.seekable.end(video.seekable.length - 1)
    : null;
}

export interface VideoEngineLevel {
  index: number;
  height: number;
}

export interface VideoEngineState {
  currentTime: number;
  duration: number;
  paused: boolean;
  ended: boolean;
  isLoading: boolean;
  buffered: number;
  volume: number;
  muted: boolean;
  playbackRate: number;
  levels: VideoEngineLevel[];
  currentLevel: number;
  autoLevel: boolean;
  error: boolean;
  /** Что сломалось, когда error: тип и детали hls.js, HTTP-код или код
   * MediaError (playerErrorDetails.ts). null, пока ошибки нет. */
  errorDetails: string | null;
  started: boolean;
  /** Live-edge position in seconds, or null before it's known / for
   * non-live sources. hls.js path: Hls.liveSyncPosition. Native Safari
   * HLS fallback: video.seekable's end. See goLive() and PlayerFooter's
   * LIVE badge. */
  liveEdge: number | null;
  /** EXT-X-TARGETDURATION of the live playlist (hls.js path only), null
   * when unknown. Scales PlayerFooter's live-edge tolerance, see liveEdge.ts. */
  liveTargetDuration: number | null;
}

export interface UseVideoEngineOptions {
  src?: string | null;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  startPosition?: number | null;
  /** CMCD contentId (Cloudflare streamCMCDAdaptiveGroups): id контента для
   * вкладки «Мониторинг» партнёрского кабинета. */
  cmcdContentId?: string | null;
}

export function useVideoEngine(options: UseVideoEngineOptions) {
  const {
    src,
    autoPlay = false,
    muted = false,
    loop = false,
    startPosition,
    cmcdContentId,
  } = options;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const lastHlsErrorRef = useRef<string | null>(null);
  // CMCD sid: один на инстанс хука (переживает смену src у того же плеера),
  // создаётся лениво при первом подключении источника, не на каждом рендере.
  const cmcdSessionIdRef = useRef<string | null>(null);
  const [state, setState] = useState<VideoEngineState>({
    currentTime: 0,
    duration: 0,
    paused: true,
    ended: false,
    isLoading: false,
    buffered: 0,
    volume: 1,
    muted,
    playbackRate: 1,
    levels: [],
    currentLevel: -1,
    autoLevel: true,
    error: false,
    errorDetails: null,
    started: false,
    liveEdge: null,
    liveTargetDuration: null,
  });

  const patch = useCallback((partial: Partial<VideoEngineState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  // Состояние принадлежит текущему src: при его смене (контент переключился
  // без remount плеера — фулскрин на контейнере должен пережить переход)
  // прогресс/ended/ошибка прошлого ролика сбрасываются синхронно, ещё в
  // рендере — ни футер, ни кнопка «Далее» не успевают увидеть устаревшее
  // состояние (ended=true прошлого видео зацикливал бы автопереходы).
  const [lastSrc, setLastSrc] = useState(src);
  if (lastSrc !== src) {
    setLastSrc(src);
    setState((prev) => ({
      ...prev,
      currentTime: 0,
      duration: 0,
      paused: true,
      ended: false,
      isLoading: false,
      buffered: 0,
      error: false,
      errorDetails: null,
      started: false,
      liveEdge: null,
      liveTargetDuration: null,
    }));
  }

  // Attach source
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    patch({ error: false, errorDetails: null, ended: false });
    lastHlsErrorRef.current = null;

    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      if (cmcdSessionIdRef.current === null)
        cmcdSessionIdRef.current = createSessionId();
      // Cloudflare Stream тарифицирует доставленные минуты, а hls.js по
      // умолчанию буферизует вперёд max(60 МБ / битрейт, 30 с) с потолком
      // 600 с: для 1080p это около двух минут, для 720p около четырёх.
      // Зритель, ушедший через полминуты, оплачивал бы их целиком. Держим
      // вперёд 20 с с жёстким потолком 40 с, сзади 30 с, чтобы не копить
      // память на длинных записях.
      const instance = new Hls({
        capLevelToPlayerSize: true,
        capLevelOnFPSDrop: true,
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        backBufferLength: 30,
        startPosition: startPosition ?? -1,
        // CMCD-телеметрия для Cloudflare (streamCMCDAdaptiveGroups): параметры
        // в query сегментов, подпись ссылок Cloudflare их не покрывает. Кабинет
        // партнёра показывает по ним зрителей, буфер и разрешения (вкладка
        // «Мониторинг»). Для других источников (Bunny) блок не передаём вовсе.
        ...(isCloudflareStreamSource(src)
          ? {
              cmcd: {
                sessionId: cmcdSessionIdRef.current,
                contentId: cmcdContentId ?? undefined,
                useHeaders: false,
              },
            }
          : {}),
      });
      instance.loadSource(src);
      instance.attachMedia(video);
      // hls.js на iOS 17+ Safari предпочитает ManagedMediaSource (MMS) и сам
      // выставляет video.disableRemotePlayback = true при её использовании
      // (buffer-controller: без этого MMS не открывается), что заодно
      // прячет кнопку AirPlay даже в нативном полноэкранном плеере.
      // Официальный приём WebKit для MSE+AirPlay (webkit.org/blog/15036):
      // второй дочерний <source> с настоящим плейлистом рядом с MSE-blob
      // источником — Safari сам предложит AirPlay и на касте переключится
      // на этот источник, буфер hls.js это не затрагивает. Добавлять его
      // нужно после MEDIA_ATTACHED: до этого момента hls.js ещё сам чистит
      // детей <video> (removeSourceChildren) при подключении своего blob.
      instance.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (!video.disableRemotePlayback) return;
        video.disableRemotePlayback = false;
        if (!video.querySelector("source[data-airplay-source]")) {
          const airplaySource = document.createElement("source");
          airplaySource.src = src;
          airplaySource.type = "application/vnd.apple.mpegurl";
          airplaySource.dataset.airplaySource = "true";
          video.appendChild(airplaySource);
        }
      });
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        patch({
          levels: instance.levels.map((level, index) => ({
            index,
            height: level.height,
          })),
        });
      });
      instance.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        patch({
          currentLevel: data.level,
          autoLevel: instance.autoLevelEnabled,
        });
      });
      // Fires whenever a live playlist's manifest is re-fetched and grows —
      // the only reliable signal for a moving live edge (there's no
      // "liveEdgeChanged" event). Harmless no-op for VOD sources (their
      // liveSyncPosition is always undefined).
      instance.on(Hls.Events.LEVEL_UPDATED, (_event, data) => {
        const position = instance.liveSyncPosition;
        const targetDuration = data.details?.targetduration;
        patch({
          liveEdge:
            typeof position === "number" && Number.isFinite(position)
              ? position
              : null,
          liveTargetDuration:
            data.details?.live &&
            typeof targetDuration === "number" &&
            Number.isFinite(targetDuration)
              ? targetDuration
              : null,
        });
      });
      instance.on(Hls.Events.ERROR, (_event, data) => {
        // Последняя ошибка hls.js, даже нефатальная: если следом упадёт
        // сам <video>, помощнику пригодится, что ему предшествовало.
        lastHlsErrorRef.current = describeHlsError(data);
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          instance.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          instance.recoverMediaError();
        } else {
          patch({
            error: true,
            errorDetails: lastHlsErrorRef.current,
            isLoading: false,
          });
        }
      });
      hls = instance;
      hlsRef.current = instance;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      if (startPosition) video.currentTime = startPosition;
    } else {
      patch({ error: true, errorDetails: "HLS is not supported" });
    }

    return () => {
      hls?.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Video element listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const prefs = readPrefs();
    video.volume = prefs.volume ?? 1;
    video.muted = muted || prefs.muted === true;
    video.loop = loop;
    patch({ volume: video.volume, muted: video.muted });

    const onTimeUpdate = () => patch({ currentTime: video.currentTime });
    // Safari native-HLS fallback (no hls.js instance controlling this
    // <video>, so no LEVEL_UPDATED to hook): video.seekable's end is the
    // same live-edge signal the hls.js path derives from liveSyncPosition.
    // No-ops while hls.js owns playback — that path is handled above.
    const patchNativeLiveEdge = () => {
      if (hlsRef.current) return;
      patch({ liveEdge: computeNativeLiveEdge(video) });
    };
    const onDurationChange = () => {
      patch({ duration: video.duration || 0 });
      patchNativeLiveEdge();
    };
    const onProgress = () => {
      const { buffered } = video;
      patch({
        buffered: buffered.length ? buffered.end(buffered.length - 1) : 0,
      });
      patchNativeLiveEdge();
    };
    const onPlay = () => patch({ paused: false, ended: false, started: true });
    const onPause = () => patch({ paused: true });
    const onEnded = () => patch({ ended: true, paused: true });
    const onWaiting = () => patch({ isLoading: true });
    const onPlaying = () => patch({ isLoading: false });
    // Native <video> error (e.g. the Safari-native-HLS fallback path with no
    // hls.js involved at all, or any decode failure hls.js itself doesn't
    // intercept) — without this, playback silently stalls with no retry UI.
    const onError = () =>
      patch({
        error: true,
        errorDetails: [describeMediaError(video.error), lastHlsErrorRef.current]
          .filter(Boolean)
          .join("; "),
        isLoading: false,
      });
    // Only patch state here — never persist. This fires for the mount-time
    // prefs restore and the autoplay-blocked mute fallback too, and those must
    // not be written back as if the user chose them. Persistence happens only
    // from the user-initiated actions below (setVolume / toggleMute).
    const onVolumeChange = () => {
      patch({ volume: video.volume, muted: video.muted });
    };
    const onRateChange = () => patch({ playbackRate: video.playbackRate });

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("progress", onProgress);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("ratechange", onRateChange);
    video.addEventListener("error", onError);
    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("ratechange", onRateChange);
      video.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const play = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
    } catch (error) {
      // Autoplay blocked: retry muted. This is a browser-imposed fallback, not a
      // user choice, so it must not be persisted as a preference (see
      // onVolumeChange above — it never persists).
      if ((error as DOMException)?.name === "NotAllowedError" && !video.muted) {
        video.muted = true;
        try {
          await video.play();
        } catch {
          // give up: user will press play
        }
      }
    }
  }, []);

  // Autoplay on mount / src change
  useEffect(() => {
    if (autoPlay && src) void play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, autoPlay]);

  const actions = {
    play,
    pause: useCallback(() => videoRef.current?.pause(), []),
    togglePlay: useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) void play();
      else video.pause();
    }, [play]),
    seek: useCallback((time: number) => {
      const video = videoRef.current;
      if (video)
        video.currentTime = Math.max(0, Math.min(time, video.duration || time));
    }, []),
    seekBy: useCallback((delta: number) => {
      const video = videoRef.current;
      if (video) video.currentTime = Math.max(0, video.currentTime + delta);
    }, []),
    setVolume: useCallback((volume: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.volume = Math.max(0, Math.min(1, volume));
      if (video.volume > 0) video.muted = false;
      // User-initiated: persist the resulting volume/mute state.
      writePrefs({ volume: video.volume, muted: video.muted });
    }, []),
    toggleMute: useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = !video.muted;
      // User-initiated: persist the resulting mute state.
      writePrefs({ volume: video.volume, muted: video.muted });
    }, []),
    setPlaybackRate: useCallback((rate: number) => {
      const video = videoRef.current;
      if (video) video.playbackRate = rate;
    }, []),
    setLevel: useCallback((index: number) => {
      const hls = hlsRef.current;
      if (hls) hls.currentLevel = index;
    }, []),
    retry: useCallback(() => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      if (hls) {
        hls.startLoad();
      } else if (video) {
        video.load();
      }
    }, []),
    // Seeks to the current live edge and resumes playback if paused.
    // Recomputes fresh from hls/video rather than trusting `state.liveEdge`
    // (which can be a render behind a fast-moving live edge).
    goLive: useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      const hls = hlsRef.current;
      const hlsPosition = hls?.liveSyncPosition;
      const target =
        typeof hlsPosition === "number" && Number.isFinite(hlsPosition)
          ? hlsPosition
          : computeNativeLiveEdge(video);
      if (target == null) return;
      video.currentTime = target;
      if (video.paused) void play();
    }, [play]),
  };

  return { videoRef, state, actions };
}
